import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown, Star } from 'lucide-react';
import {
  DumbbellIcon, RunningIcon, CyclingIcon, SwimmingIcon, WalkingIcon,
  HikingIcon, RowingIcon, StairClimberIcon, EllipticalIcon,
  HIITIcon, CrossFitIcon, YogaIcon, PilatesIcon, StretchingIcon, CalisthenicsIcon,
  BoxingIcon, MartialArtsIcon, WrestlingIcon,
  TennisIcon, BadmintonIcon, BasketballIcon, SoccerIcon, VolleyballIcon, GolfIcon, TableTennisIcon,
  RockClimbingIcon, SkiingIcon, SnowboardingIcon, IceSkatingIcon, SurfingIcon, KayakingIcon,
  DanceIcon, JumpRopeIcon, SkatingIcon, ActivityIcon, ImportIcon,
} from '@/components/icons/SportIcons';

type SportIcon = React.FC<{ size?: number; className?: string }>;

export interface ActivityItem {
  id: string;
  label: string;
  icon: SportIcon;
  action: 'gym' | 'running' | 'import' | 'custom';
}

const ALL_ACTIVITIES: ActivityItem[] = [
  // Core
  { id: 'gym', label: 'Gym Workout', icon: DumbbellIcon, action: 'gym' },
  { id: 'running', label: 'Running', icon: RunningIcon, action: 'running' },

  // Cardio
  { id: 'cycling', label: 'Cycling', icon: CyclingIcon, action: 'custom' },
  { id: 'swimming', label: 'Swimming', icon: SwimmingIcon, action: 'custom' },
  { id: 'walking', label: 'Walking', icon: WalkingIcon, action: 'custom' },
  { id: 'hiking', label: 'Hiking', icon: HikingIcon, action: 'custom' },
  { id: 'rowing', label: 'Rowing', icon: RowingIcon, action: 'custom' },
  { id: 'stairclimber', label: 'Stair Climber', icon: StairClimberIcon, action: 'custom' },
  { id: 'elliptical', label: 'Elliptical', icon: EllipticalIcon, action: 'custom' },

  // Training
  { id: 'hiit', label: 'HIIT', icon: HIITIcon, action: 'custom' },
  { id: 'crossfit', label: 'CrossFit', icon: CrossFitIcon, action: 'custom' },
  { id: 'yoga', label: 'Yoga', icon: YogaIcon, action: 'custom' },
  { id: 'pilates', label: 'Pilates', icon: PilatesIcon, action: 'custom' },
  { id: 'stretching', label: 'Stretching', icon: StretchingIcon, action: 'custom' },
  { id: 'calisthenics', label: 'Calisthenics', icon: CalisthenicsIcon, action: 'custom' },

  // Combat
  { id: 'boxing', label: 'Boxing', icon: BoxingIcon, action: 'custom' },
  { id: 'martial_arts', label: 'Martial Arts', icon: MartialArtsIcon, action: 'custom' },
  { id: 'wrestling', label: 'Wrestling', icon: WrestlingIcon, action: 'custom' },

  // Sports
  { id: 'tennis', label: 'Tennis', icon: TennisIcon, action: 'custom' },
  { id: 'badminton', label: 'Badminton', icon: BadmintonIcon, action: 'custom' },
  { id: 'basketball', label: 'Basketball', icon: BasketballIcon, action: 'custom' },
  { id: 'soccer', label: 'Soccer', icon: SoccerIcon, action: 'custom' },
  { id: 'volleyball', label: 'Volleyball', icon: VolleyballIcon, action: 'custom' },
  { id: 'golf', label: 'Golf', icon: GolfIcon, action: 'custom' },
  { id: 'table_tennis', label: 'Table Tennis', icon: TableTennisIcon, action: 'custom' },

  // Outdoor / Winter
  { id: 'rock_climbing', label: 'Rock Climbing', icon: RockClimbingIcon, action: 'custom' },
  { id: 'skiing', label: 'Skiing', icon: SkiingIcon, action: 'custom' },
  { id: 'snowboarding', label: 'Snowboarding', icon: SnowboardingIcon, action: 'custom' },
  { id: 'ice_skating', label: 'Ice Skating', icon: IceSkatingIcon, action: 'custom' },
  { id: 'surfing', label: 'Surfing', icon: SurfingIcon, action: 'custom' },
  { id: 'kayaking', label: 'Kayaking', icon: KayakingIcon, action: 'custom' },

  // Other
  { id: 'dance', label: 'Dance', icon: DanceIcon, action: 'custom' },
  { id: 'jump_rope', label: 'Jump Rope', icon: JumpRopeIcon, action: 'custom' },
  { id: 'skating', label: 'Skating', icon: SkatingIcon, action: 'custom' },
  { id: 'other', label: 'Other Activity', icon: ActivityIcon, action: 'custom' },

  // Import
  { id: 'import', label: 'Import from Chat', icon: ImportIcon, action: 'import' },
];

const FAVORITES_KEY = 'straiv_favorite_activities';

function loadFavorites(): string[] {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveFavorites(ids: string[]) {
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(ids));
}

interface ActivityPickerDropdownProps {
  onSelectActivity: (activity: ActivityItem) => void;
}

export const ActivityPickerDropdown = ({ onSelectActivity }: ActivityPickerDropdownProps) => {
  const [favorites, setFavorites] = useState<string[]>(loadFavorites);

  const toggleFavorite = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    setFavorites(prev => {
      const next = prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id];
      saveFavorites(next);
      return next;
    });
  };

  const { favoriteItems, otherItems } = useMemo(() => {
    const favSet = new Set(favorites);
    const favoriteItems = ALL_ACTIVITIES.filter(a => favSet.has(a.id));
    const otherItems = ALL_ACTIVITIES.filter(a => !favSet.has(a.id));
    return { favoriteItems, otherItems };
  }, [favorites]);

  const renderItem = (activity: ActivityItem) => {
    const Icon = activity.icon;
    const isFav = favorites.includes(activity.id);

    return (
      <DropdownMenuItem
        key={activity.id}
        onClick={() => onSelectActivity(activity)}
        className="py-3 cursor-pointer flex items-center justify-between"
      >
        <span className="flex items-center">
          <Icon size={16} className="mr-3 text-primary shrink-0" />
          {activity.label}
        </span>
        <button
          onClick={(e) => toggleFavorite(e, activity.id)}
          className="ml-2 p-0.5 rounded hover:bg-accent"
        >
          <Star
            className={`h-3.5 w-3.5 transition-colors ${isFav ? 'fill-primary text-primary' : 'text-muted-foreground/40'}`}
          />
        </button>
      </DropdownMenuItem>
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="bg-gradient-primary text-black shadow-glow hover:shadow-lg transition-all duration-300 px-8 py-6 text-base font-semibold">
          <ActivityIcon size={20} className="mr-2" />
          Log Workout
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="center"
        className="w-64 bg-popover border-border max-h-[70vh] overflow-y-auto z-50 !backdrop-blur-none"
        style={{ backgroundColor: 'hsl(var(--popover))' }}
      >
        {favoriteItems.length > 0 && (
          <>
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal uppercase tracking-wider">
              Favorites
            </DropdownMenuLabel>
            {favoriteItems.map(renderItem)}
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal uppercase tracking-wider">
          All Activities
        </DropdownMenuLabel>
        {otherItems.map(renderItem)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
